import React, { useEffect, useImperativeHandle, forwardRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  VStack,
  Select,
  Text,
  Box,
  Switch,
  Flex,
  useColorModeValue,
  HStack,
  Icon,
  Spinner,
} from '@chakra-ui/react';
import { FaCreditCard, FaKey } from 'react-icons/fa';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  createPaymentGateway,
  updatePaymentGateway,
  getPaymentGatewayTypes,
} from '../../services/paymentGatewayService';
import toast from 'react-hot-toast';

const SECRET_KEYS = new Set(['password', 'apiKey', 'clientSecret']);

function buildDefaultConfig(fields = [], existingConfig = {}) {
  const config = {};
  for (const field of fields) {
    if (field.inputType === 'boolean') {
      if (existingConfig[field.key] !== undefined) {
        config[field.key] = Boolean(existingConfig[field.key]);
      } else {
        config[field.key] = field.defaultValue !== undefined ? Boolean(field.defaultValue) : false;
      }
      continue;
    }
    if (SECRET_KEYS.has(field.key)) {
      config[field.key] = '';
      continue;
    }
    const existing = existingConfig[field.key];
    if (existing !== undefined && existing !== null && existing !== '__SET__') {
      config[field.key] = existing;
    } else if (field.defaultValue !== undefined) {
      config[field.key] = field.defaultValue;
    } else {
      config[field.key] = '';
    }
  }
  return config;
}

const PaymentGatewayForm = forwardRef(({ gateway, onSuccess }, ref) => {
  const queryClient = useQueryClient();
  const inputBg = useColorModeValue('gray.50', 'gray.900');
  const sectionBg = useColorModeValue('white', 'whiteAlpha.50');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const commonInputProps = {
    bg: inputBg,
    border: '1px solid',
    borderColor: 'transparent',
    borderRadius: 'xl',
    _focus: {
      borderColor: 'blue.400',
      boxShadow: '0 0 0 1px #3b82f6',
      bg: useColorModeValue('white', 'gray.800'),
    },
    _hover: {
      borderColor: useColorModeValue('gray.300', 'gray.600'),
    },
    transition: 'all 0.2s',
  };

  const { data: typesData, isLoading: typesLoading } = useQuery(
    ['payment-gateway-types'],
    getPaymentGatewayTypes,
    { staleTime: 10 * 60 * 1000 }
  );

  const typeMetaList = typesData?.data || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
    reset,
  } = useForm({
    defaultValues: {
      name: gateway?.name || '',
      type: gateway?.type || '',
      isActive: gateway?.isActive !== undefined ? gateway.isActive : true,
      config: {},
    },
    mode: 'onChange',
  });

  const watchedType = watch('type');
  const selectedMeta = typeMetaList.find((t) => t.type === watchedType);

  useEffect(() => {
    if (!typeMetaList.length) return;
    const meta = typeMetaList.find((t) => t.type === (gateway?.type || watchedType));
    const fields = meta?.fields || [];
    reset({
      name: gateway?.name || '',
      type: gateway?.type || '',
      isActive: gateway?.isActive !== undefined ? gateway.isActive : true,
      config: buildDefaultConfig(fields, gateway?.config || {}),
    });
    // فقط وقتی gateway یا لیست types عوض شد
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateway, typeMetaList]);

  useEffect(() => {
    if (!watchedType || !selectedMeta) return;
    // هنگام تعویض type در حالت ایجاد، config را ریست کن
    if (gateway && gateway.type === watchedType) return;
    if (gateway && gateway.type !== watchedType) {
      setValue('config', buildDefaultConfig(selectedMeta.fields, {}));
      return;
    }
    if (!gateway) {
      setValue('config', buildDefaultConfig(selectedMeta.fields, {}));
    }
  }, [watchedType, selectedMeta, gateway, setValue]);

  const createMutation = useMutation(createPaymentGateway, {
    onSuccess: () => {
      toast.success('درگاه پرداخت با موفقیت اضافه شد');
      queryClient.invalidateQueries(['payment-gateways']);
      if (onSuccess) onSuccess();
    },
    onError: (error) =>
      toast.error(error.response?.data?.message || 'خطا در افزودن درگاه پرداخت'),
  });

  const updateMutation = useMutation(
    ({ id, data }) => updatePaymentGateway(id, data),
    {
      onSuccess: () => {
        toast.success('درگاه پرداخت با موفقیت بروزرسانی شد');
        queryClient.invalidateQueries(['payment-gateways']);
        if (onSuccess) onSuccess();
      },
      onError: (error) =>
        toast.error(error.response?.data?.message || 'خطا در بروزرسانی درگاه پرداخت'),
    }
  );

  const handleFormSubmit = (data) => {
    const meta = typeMetaList.find((t) => t.type === data.type);
    const config = { ...(data.config || {}) };

    if (meta) {
      for (const field of meta.fields) {
        if (!SECRET_KEYS.has(field.key)) continue;
        const value = config[field.key];
        if (value === undefined || value === null || String(value).trim() === '') {
          delete config[field.key];
        }
      }
    }

    const payload = {
      name: data.name,
      type: data.type,
      isActive: data.isActive,
      config,
    };

    if (gateway) {
      updateMutation.mutate({ id: gateway._id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  useImperativeHandle(ref, () => ({
    handleSubmit: () => handleSubmit(handleFormSubmit)(),
  }));

  if (typesLoading) {
    return (
      <Flex align="center" justify="center" py={10}>
        <Spinner color="blue.500" />
      </Flex>
    );
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <VStack spacing={6} align="stretch" pb={4}>
        <Box bg={sectionBg} p={5} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}>
          <HStack mb={4}>
            <Icon as={FaCreditCard} color="blue.500" />
            <Text fontWeight="bold" fontSize="md" color={textColor}>
              اطلاعات پایه درگاه پرداخت
            </Text>
          </HStack>
          <VStack spacing={4}>
            <FormControl isInvalid={errors.name}>
              <FormLabel fontSize="sm" fontWeight="bold">
                نام درگاه
              </FormLabel>
              <Input
                {...commonInputProps}
                {...register('name', { required: 'نام درگاه الزامی است' })}
                placeholder="مثال: زرین‌پال فروشگاه اصلی"
              />
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.type}>
              <FormLabel fontSize="sm" fontWeight="bold">
                نوع درگاه
              </FormLabel>
              <Select
                {...commonInputProps}
                {...register('type', { required: 'نوع درگاه الزامی است' })}
                isDisabled={Boolean(gateway)}
              >
                <option value="">انتخاب نوع درگاه...</option>
                {typeMetaList.map((t) => (
                  <option key={t.type} value={t.type}>
                    {t.label}
                  </option>
                ))}
              </Select>
              {gateway && (
                <FormHelperText color={mutedColor}>
                  نوع درگاه پس از ایجاد قابل تغییر نیست.
                </FormHelperText>
              )}
              <FormErrorMessage>{errors.type?.message}</FormErrorMessage>
            </FormControl>

            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel fontSize="sm" fontWeight="bold" mb={0}>
                فعال باشد
              </FormLabel>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <Switch
                    colorScheme="green"
                    isChecked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            </FormControl>
          </VStack>
        </Box>

        {selectedMeta && (
          <Box bg={sectionBg} p={5} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}>
            <HStack mb={4}>
              <Icon as={FaKey} color="teal.500" />
              <Text fontWeight="bold" fontSize="md" color={textColor}>
                پارامترهای {selectedMeta.label}
              </Text>
            </HStack>
            <VStack spacing={4}>
              {selectedMeta.fields.map((field) => {
                if (field.inputType === 'boolean') {
                  return (
                    <FormControl
                      key={field.key}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <FormLabel fontSize="sm" fontWeight="bold" mb={0}>
                        {field.label}
                      </FormLabel>
                      <Controller
                        name={`config.${field.key}`}
                        control={control}
                        render={({ field: ctrl }) => (
                          <Switch
                            colorScheme="blue"
                            isChecked={Boolean(ctrl.value)}
                            onChange={(e) => ctrl.onChange(e.target.checked)}
                          />
                        )}
                      />
                    </FormControl>
                  );
                }

                const isSecret = SECRET_KEYS.has(field.key);
                const wasSet = gateway?.config?.[field.key] === '__SET__';

                return (
                  <FormControl
                    key={field.key}
                    isInvalid={errors.config?.[field.key]}
                    isRequired={field.required && !gateway}
                  >
                    <FormLabel fontSize="sm" fontWeight="bold">
                      {field.label}
                    </FormLabel>
                    <Input
                      {...commonInputProps}
                      type={isSecret ? 'password' : field.inputType === 'url' ? 'url' : 'text'}
                      dir="ltr"
                      textAlign="left"
                      placeholder={
                        isSecret && wasSet
                          ? 'در صورت خالی ماندن، مقدار قبلی حفظ می‌شود'
                          : field.inputType === 'url'
                            ? 'https://...'
                            : ''
                      }
                      {...register(`config.${field.key}`, {
                        required:
                          field.required && !gateway
                            ? `${field.label} الزامی است`
                            : false,
                      })}
                    />
                    {isSecret && wasSet && (
                      <FormHelperText color={mutedColor}>
                        قبلاً ذخیره شده؛ برای تغییر مقدار جدید وارد کنید.
                      </FormHelperText>
                    )}
                    <FormErrorMessage>
                      {errors.config?.[field.key]?.message}
                    </FormErrorMessage>
                  </FormControl>
                );
              })}
            </VStack>
          </Box>
        )}
      </VStack>
    </form>
  );
});

PaymentGatewayForm.displayName = 'PaymentGatewayForm';

export default PaymentGatewayForm;
